import React, { useRef, useEffect, useCallback, memo } from 'react'
import {
  SearchInputContainer,
  SearchInputField,
  SearchIcon,
  ClearButton,
  ClearIcon,
} from '../styles'

type SearchInputProps = {
  value: string
  onChange: (value: string) => void
  onFocus?: () => void
  onBlur?: () => void
  onMouseDown?: (e: React.MouseEvent) => void
  placeholder?: string
  autoFocus?: boolean
}

export const SearchInput = memo<SearchInputProps>(
  ({
    value,
    onChange,
    onFocus,
    onBlur,
    onMouseDown,
    placeholder = 'Search...',
    autoFocus = false,
  }) => {
    const inputRef = useRef<HTMLInputElement>(null)

    useEffect(() => {
      if (autoFocus && inputRef.current) {
        inputRef.current.focus()
      }
    }, [autoFocus])

    const handleChange = useCallback(
      (e: React.ChangeEvent<HTMLInputElement>): void => {
        onChange(e.target.value)
      },
      [onChange]
    )

    const handleClear = useCallback((): void => {
      onChange('')
      inputRef.current?.focus()
    }, [onChange])

    const preventBlur = useCallback((e: React.MouseEvent): void => {
      e.preventDefault()
    }, [])

    const handleKeyDown = useCallback(
      (e: React.KeyboardEvent): void => {
        if (e.key === 'Escape' && value) {
          e.preventDefault()
          handleClear()
        }
      },
      [value, handleClear]
    )

    const handleContainerMouseDown = useCallback(
      (e: React.MouseEvent): void => {
        if (onMouseDown) {
          onMouseDown(e)
        }
        if (e.target !== inputRef.current && inputRef.current) {
          inputRef.current.focus()
        }
      },
      [onMouseDown]
    )

    return (
      <SearchInputContainer onMouseDown={handleContainerMouseDown}>
        <SearchIcon aria-hidden="true">
          <svg
            width="20"
            height="20"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
          >
            <circle cx="11" cy="11" r="8" />
            <path d="M21 21l-4.35-4.35" />
          </svg>
        </SearchIcon>
        <SearchInputField
          ref={inputRef}
          type="text"
          value={value}
          onChange={handleChange}
          onFocus={onFocus}
          onBlur={onBlur}
          onMouseDown={onMouseDown}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
          aria-label="Search"
          autoComplete="off"
          autoCorrect="off"
          autoCapitalize="off"
          spellCheck="false"
        />
        {value && (
          <ClearButton
            type="button"
            onClick={handleClear}
            onMouseDown={preventBlur}
            aria-label="Clear search"
          >
            <ClearIcon aria-hidden="true">
              <svg
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
              >
                <path d="M18 6L6 18M6 6l12 12" />
              </svg>
            </ClearIcon>
          </ClearButton>
        )}
      </SearchInputContainer>
    )
  }
)

SearchInput.displayName = 'SearchInput'
